import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';
import ThemeAngleSelectionModal from "@pages/design-management/page-design/components/ThemeAngleSelectionModal.jsx";
import themeAngleMappingApi from "@services/themeAngleMappingApi.jsx";
import AlertModal from '@components/common/AlertModal';
import ConfirmModal from '@components/common/ConfirmModal';
import CameraAngleDetailModal from '@pages/camera-angle-management/components/CameraAngleDetailModal';

const PrintModeSetting = ({ themeId, themePageMaster }) => {

    const themePageId = themePageMaster?.id || null;
    
    console.log('StartSetting props:', { themeId, themePageMaster, themePageId });

    // 상태 관리
    const [startPage, setStartPage] = useState({
        themePageMaster: themePageMaster,
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [themeAngleMappings, setThemeAngleMappings] = useState([]);
    const [availableAngles, setAvailableAngles] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    
    // 모달 상태
    const [showThemeAngleModal, setShowThemeAngleModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [editTargetMapping, setEditTargetMapping] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [currentAngle, setCurrentAngle] = useState(null);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    // API 호출로 데이터 가져오기
    useEffect(() => {
        const fetchPrintWaitingPage = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await designApi.getStartPage(themeId);

                setStartPage({
                    ...data
                });

                console.log("API 호출 성공:", data);
            } catch (err) {
                console.error("API 호출 실패:", err);
                setError(err);
                // 에러 발생 시 기본 데이터 유지
            } finally {
                setIsLoading(false);
            }
        };

        fetchPrintWaitingPage();
    }, [themeId]);

    const fetchThemeAngleMappings = async () => {
        try {
            // themePageId가 없으면 API 호출하지 않음
            if (!themePageId) {
                console.warn('themePageId가 없어서 앵글 목록을 가져올 수 없습니다.');
                return;
            }

            const mappings = await themeAngleMappingApi.getThemeAngleMappings(themeId, themePageId);
            console.log(mappings)
            setThemeAngleMappings(mappings || []);
        } catch (error) {
            console.error('앵글 목록 조회 실패:', error);
        }
    };

    const fetchAvailableAngles = async () => {
        try {
            console.log("앵글 목록 요청 시작, themeId:", themeId);
            // isGreeting이 true인 앵글만 조회
            const response = await themeAngleMappingApi.getAvailableAngles(themeId, {
                isGreeting: true
            });
            console.log("앵글 목록 응답:", response);

            // response가 배열인지 객체인지 확인
            let angles = [];
            if (Array.isArray(response)) {
                angles = response;
            } else if (response && response.data) {
                angles = response.data;
            } else if (response && response.success && response.data) {
                angles = response.data;
            }

            setAvailableAngles(angles);
            console.log("사용 가능한 앵글 설정 완료 (Greeting 타입만):", angles);
        } catch (err) {
            console.error("사용 가능한 앵글 로드 실패:", err);
            setAvailableAngles([]);
        }
    };

    // 앵글 목록 가져오기
    useEffect(() => {
        // themeId는 필수, themePageId는 선택적
        if (themeId) {
            // availableAngles는 themePageId 없이도 가져올 수 있음
            fetchAvailableAngles();
            
            // themeAngleMappings는 themePageId가 있을 때만
            if (themePageId) {
                fetchThemeAngleMappings();
            }
        }
    }, [themeId, themePageId]);

    // 입력값 변경 핸들러
    const handleInputChange = (field, value) => {
        setStartPage(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 리소스 업로드 처리 함수
    const handleResourceUploaded = (resourceType, resourceId) => {
        setStartPage({
            ...startPage,
            [resourceType]: resourceId
        });
    };

    const handleCenterAlign = (type) => {
        const centerX = 500;
        const centerY = 450;

        if (type === "startButton") {
            setStartPage({ startButtonPositionX: centerX, startButtonPositionY: centerY });
        } else if (type === "logoButton") {
            setStartPage({ logoPositionX: centerX, logoPositionY: centerY });
        }
    };
    
    // 앵글 관련 핸들러 함수들
    const handleAddAngle = () => {
        // 앵글 refresh
        fetchAvailableAngles();

        setEditTargetMapping(null);
        setIsEditMode(false);
        setShowThemeAngleModal(true);
    };

    const handleEditAngle = (mapping) => {
        setEditTargetMapping(mapping);
        setIsEditMode(true);
        setShowThemeAngleModal(true);
    };

    const handleDeleteAngle = (mappingId) => {
        setDeleteTargetId(mappingId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            const result = await themeAngleMappingApi.removeAngleFromTheme(deleteTargetId);
            if (result) {
                setThemeAngleMappings(themeAngleMappings.filter(m => m.id !== deleteTargetId));
                setAlertMessage('앵글이 삭제되었습니다.');
                setShowAlertModal(true);
            }
        } catch (error) {
            console.error('앵글 삭제 실패:', error);
            setAlertMessage('앵글 삭제에 실패했습니다.');
            setShowAlertModal(true);
        } finally {
            setShowDeleteModal(false);
            setDeleteTargetId(null);
        }
    };

    const handleAngleSelected = async (result) => {
        try {
            // themePageId가 있을 때만 앵글 목록 다시 불러오기
            if (themePageId) {
                const mappings = await themeAngleMappingApi.getThemeAngleMappings(themeId, themePageId);
                setThemeAngleMappings(mappings || []);
            }
            
            setAlertMessage(isEditMode ? '앵글이 수정되었습니다.' : '앵글이 추가되었습니다.');
            setShowAlertModal(true);
        } catch (error) {
            console.error('앵글 목록 새로고침 실패:', error);
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        if (isChecked) {
            setSelectedItems(themeAngleMappings.map(item => item.id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleItemSelect = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleDetailClick = (angle) => {
        setCurrentAngle(angle);
        setShowDetailModal(true);
    };
    
    // 체크박스 변경 핸들러
    const handleCheckboxChange = (mappingId) => {
        setThemeAngleMappings(prevMappings =>
            prevMappings.map(mapping =>
                mapping.id === mappingId
                    ? { ...mapping, selected: !mapping.selected }
                    : mapping
            )
        );
    };
    
    // 상세정보 보기 핸들러
    const handleInfoClick = (mapping) => {
        setCurrentAngle(mapping.cameraAngle || mapping);
        setShowDetailModal(true);
    };
    
    // 앵글 타입 표시 헬퍼
    const getAngleTypeDisplay = (angle) => {
        if (!angle) return '-';

        if (angle.isGreeting) {
            return 'Greeting';
        } else if (angle.isPhoto && angle.isVideo) {
            return '사진+동영상';
        } else if (angle.isPhoto) {
            return '사진';
        } else if (angle.isVideo) {
            return '동영상';
        } else {
            return '-';
        }
    };

    // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
    useEffect(() => {
        const eventName = `savePageDesign_start`; // 각 컴포넌트마다 고유한 이벤트 이름

        const saveHandler = async (event) => {
            try {
                // 저장 중임을 표시
                setIsLoading(true);

                // 자체 로직을 사용하여 데이터 저장
                const dataToSave = {
                    ...startPage,
                    themeId: event.detail.themeId // themeId를 명시적으로 포함
                };
                const isSucceed = await designApi.saveStartPage(event.detail.themeId, dataToSave);

                if(isSucceed) {
                    // 성공 메시지
                    alert("저장되었습니다.");
                } else {
                    // 실패 메시지
                    alert("저장에 실패했습니다.");
                }

            } catch (error) {
                // 오류 처리
                console.error("저장 실패:", error);
                alert("저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        // 이벤트 리스너 등록
        document.addEventListener(eventName, saveHandler);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [startPage, themeId]); // 의존성 배열에 필요한 상태 포함

    return (
        <>
            <div className="design-settings">
                {/* 언어 선택 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">언어 선택</h4>
                    <div className="option-select-container">
                        <select
                            className="option-select"
                            value={startPage.languageCode || "ko"}
                            onChange={(e) =>
                                handleInputChange(
                                    "languageCode",
                                    e.target.value
                                )
                            }
                        >
                            <option value="ko">한국어</option>
                            <option value="en">영어</option>
                            <option value="jp">일본어</option>
                            <option value="cn">중국어</option>
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>

                {/* 배경 이미지 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">배경 이미지</h4>
                    <ResourceUploadField
                        label=""
                        resourceId={startPage.backgroundResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded("backgroundResourceId", id)
                        }
                        acceptTypes="image/*"
                    />
                </div>

                {/* 배경 음악 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">배경 음악</h4>
                    <ResourceUploadField
                        label=""
                        resourceId={startPage.backgroundMusicResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded(
                                "backgroundMusicResourceId",
                                id
                            )
                        }
                        acceptTypes="audio/*"
                    />
                </div>

                {/* 효과음 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">효과음</h4>
                    <ResourceUploadField
                        label="버튼클릭"
                        resourceId={startPage.buttonClickSoundResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded(
                                "buttonClickSoundResourceId",
                                id
                            )
                        }
                        acceptTypes="audio/*"
                    />
                    <ResourceUploadField
                        label="에러효과음"
                        resourceId={startPage.errorSoundResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded("errorSoundResourceId", id)
                        }
                        acceptTypes="audio/*"
                    />
                </div>

                {/* 시작버튼 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">시작버튼</h4>
                    <ResourceUploadField
                        label=""
                        resourceId={startPage.startButtonResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded("startButtonResourceId", id)
                        }
                        acceptTypes="image/*"
                    />
                    <div className="settings-row">
                        <div className="settings-label">위치</div>
                        <div className="position-component">
                            <div className="position-input-group">
                                <span className="position-label">X</span>
                                <input
                                    type="text"
                                    className="position-input"
                                    id="start-btn-x"
                                    value={startPage.startButtonPositionX || 0}
                                    onChange={(e) =>
                                        setStartPage({
                                            ...startPage,
                                            startButtonPositionX:
                                                e.target.value,
                                        })
                                    }
                                />
                                <span className="position-label">Y</span>
                                <input
                                    type="text"
                                    className="position-input"
                                    id="start-btn-y"
                                    value={startPage.startButtonPositionY || 0}
                                    onChange={(e) =>
                                        setStartPage({
                                            ...startPage,
                                            startButtonPositionY:
                                                e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="direction-buttons">
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="up"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                startButtonPositionY:
                                                    startPage.startButtonPositionY -
                                                    1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-up-s-fill"></i>
                                    </button>
                                </div>
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="left"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                startButtonPositionX:
                                                    startPage.startButtonPositionX -
                                                    1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-left-s-fill"></i>
                                    </button>
                                    <button
                                        className="direction-btn"
                                        data-direction="right"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                startButtonPositionX:
                                                    startPage.startButtonPositionX +
                                                    1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-right-s-fill"></i>
                                    </button>
                                </div>
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="down"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                startButtonPositionY:
                                                    startPage.startButtonPositionY +
                                                    1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-down-s-fill"></i>
                                    </button>
                                </div>
                            </div>
                            <button
                                className="btn center-btn"
                                onClick={() => handleCenterAlign("startButton")}
                            >
                                중앙정렬
                            </button>
                        </div>
                    </div>
                </div>

                {/* 로고 섹션 */}
                <div className="settings-box">
                    <h4 className="settings-title">로고</h4>
                    <ResourceUploadField
                        label=""
                        resourceId={startPage.logoResourceId}
                        onResourceUploaded={(id) =>
                            handleResourceUploaded("logoResourceId", id)
                        }
                        acceptTypes="image/*"
                    />
                    <div className="settings-row">
                        <div className="settings-label">위치</div>
                        <div className="position-component">
                            <div className="position-input-group">
                                <span className="position-label">X</span>
                                <input
                                    type="text"
                                    className="position-input"
                                    id="start-btn-x"
                                    value={startPage.logoPositionX || 0}
                                    onChange={(e) =>
                                        setStartPage({
                                            ...startPage,
                                            logoPositionX: e.target.value,
                                        })
                                    }
                                />
                                <span className="position-label">Y</span>
                                <input
                                    type="text"
                                    className="position-input"
                                    id="start-btn-y"
                                    value={startPage.logoPositionY || 0}
                                    onChange={(e) =>
                                        setStartPage({
                                            ...startPage,
                                            logoPositionY: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="direction-buttons">
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="up"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                logoPositionY:
                                                    startPage.logoPositionY - 1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-up-s-fill"></i>
                                    </button>
                                </div>
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="left"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                logoPositionX:
                                                    startPage.logoPositionX - 1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-left-s-fill"></i>
                                    </button>
                                    <button
                                        className="direction-btn"
                                        data-direction="right"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                logoPositionX:
                                                    startPage.logoPositionX + 1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-right-s-fill"></i>
                                    </button>
                                </div>
                                <div className="direction-row">
                                    <button
                                        className="direction-btn"
                                        data-direction="down"
                                        onClick={() =>
                                            setStartPage({
                                                ...startPage,
                                                logoPositionY:
                                                    startPage.logoPositionY + 1,
                                            })
                                        }
                                    >
                                        <i className="ri-arrow-down-s-fill"></i>
                                    </button>
                                </div>
                            </div>
                            <button
                                className="btn center-btn"
                                onClick={() => handleCenterAlign("logoButton")}
                            >
                                중앙정렬
                            </button>
                        </div>
                    </div>
                </div>

                <div className="data-section-header">
                    <div className="data-section-title">
                        <h4 className="section-title">앵글</h4>
                        <div className="section-title-icon">
                            <a
                                href="#"
                                className="angle-add-btn"
                                title="앵글 추가"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAddAngle();
                                }}
                            >
                                <i className="ri-add-circle-line"></i>
                            </a>
                        </div>
                    </div>
                    <div className="filter-dropdown-container">
                        <div className="filter-dropdown">
                            <select id="angle-filter">
                                <option value="all">전체</option>
                                <option value="photo">사진</option>
                                <option value="video">동영상</option>
                                <option value="combined">동영상 + 사진</option>
                                <option value="greeting">greeting</option>
                            </select>
                        </div>
                    </div>
                </div>
                
                {/* 데이터 테이블 섹션 */}
                <div className="common-table-container">
                    <table className="common-table">
                        <thead>
                            <tr>
                                <th>
                                    <div className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            id="select-all"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        <label htmlFor="select-all" className="custom-checkbox"></label>
                                    </div>
                                </th>
                                <th>NO</th>
                                <th>앵글명</th>
                                <th>앵글 타입</th>
                                <th>상세정보</th>
                                <th>수정</th>
                                <th>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {themeAngleMappings.map((mapping) => (
                                <tr key={mapping.id}>
                                    <td>
                                        <div className="checkbox-container">
                                            <input
                                                type="checkbox"
                                                id={`mapping-${mapping.id}`}
                                                checked={mapping.selected || false}
                                                onChange={() => handleCheckboxChange(mapping.id)}
                                            />
                                            <label htmlFor={`mapping-${mapping.id}`} className="custom-checkbox"></label>
                                        </div>
                                    </td>
                                    <td>{mapping.displayOrder}</td>
                                    <td>{mapping.angleName || mapping.cameraAngle?.angleName || '-'}</td>
                                    <td>{mapping.formattedAngleType || mapping.angleTypeDescription}</td>
                                    <td><button className="info-btn" onClick={() => handleInfoClick(mapping)}><i className="ri-information-line"></i></button></td>
                                    <td><button className="edit-btn" onClick={() => handleEditAngle(mapping)}><i className="ri-pencil-line"></i></button></td>
                                    <td><button className="delete-btn" onClick={() => handleDeleteAngle(mapping.id)}><i className="ri-close-line"></i></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 모달들 */}
            {showThemeAngleModal && (
                <ThemeAngleSelectionModal
                    themePageMaster={themePageMaster || { id: 'start-page' }}
                    themeId={themeId}
                    availableAngles={availableAngles}
                    onClose={() => {
                        setShowThemeAngleModal(false);
                        setEditTargetMapping(null);
                        setIsEditMode(false);
                    }}
                    onSave={handleAngleSelected}
                    isEditMode={isEditMode}
                    initialData={editTargetMapping}
                />
            )}

            {showDeleteModal && (
                <ConfirmModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDelete}
                    title="앵글 삭제"
                    message="선택한 앵글을 삭제하시겠습니까?"
                />
            )}

            {showDetailModal && currentAngle && (
                <CameraAngleDetailModal
                    isVisible={showDetailModal}
                    cameraAngle={currentAngle}
                    onClose={() => {
                        setShowDetailModal(false);
                        setCurrentAngle(null);
                    }}
                />
            )}

            {showAlertModal && (
                <AlertModal
                    isOpen={showAlertModal}
                    onClose={() => setShowAlertModal(false)}
                    message={alertMessage}
                />
            )}
        </>
    );
};

export default PrintModeSetting;